import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import Dropdown from "../../components/Dropdown";
import { formatSwedishTime } from "../../helpers/DateTime";
import { chartAreaFill, chartGradientDefaults } from "../../theme/gitoColors";
import { useTranslation } from "react-i18next";

const GRANULARITY_OPTIONS = ["15 min", "Day", "Month"];
const STEP_15_MS = 15 * 60 * 1000;

function toMs(dateLike) {
    if (!dateLike) return null;
    const d = new Date(String(dateLike));
    const t = d.getTime();
    return Number.isFinite(t) ? t : null;
}

function bucketStart(ts, granularity) {
    const d = new Date(ts);

    if (granularity === "15 min") {
        return Math.floor(ts / STEP_15_MS) * STEP_15_MS;
    }

    if (granularity === "Day") {
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }

    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function stepMs(granularity, sampleTs) {
    if (granularity === "15 min") return STEP_15_MS;
    if (granularity === "Day") return 24 * 60 * 60 * 1000;

    const d = new Date(sampleTs);
    const cur = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0).getTime();
    const next = new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0).getTime();
    return Math.max(1, next - cur);
}

function formatX(val, granularity) {
    const dt = new Date(val);

    if (granularity === "15 min") {
        return formatSwedishTime(dt, false);
    }

    if (granularity === "Day") {
        return dt.toLocaleDateString("sv-SE");
    }

    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

function toFifteenMinPoints(rows) {
    const buckets = new Map();

    for (const r of rows) {
        const x = toMs(r?.timeStamp);
        const y = Number(r?.value);

        if (x == null || !Number.isFinite(y)) continue;

        const b = Math.floor(x / STEP_15_MS) * STEP_15_MS;
        const prev = buckets.get(b);
        if (prev === undefined) buckets.set(b, y);
        else buckets.set(b, Math.max(prev, y));
    }

    return Array.from(buckets.entries())
        .map(([x, y]) => ({ x, y }))
        .sort((a, b) => a.x - b.x);
}

function maxByPeriod(points, granularity) {
    const buckets = new Map();

    for (const p of points) {
        const b = bucketStart(p.x, granularity);
        const prev = buckets.get(b);
        if (prev === undefined) buckets.set(b, p.y);
        else buckets.set(b, Math.max(prev, p.y));
    }

    return Array.from(buckets.entries())
        .map(([x, y]) => ({ x, y }))
        .sort((a, b) => a.x - b.x);
}

function fillGaps(pts, granularity) {
    if (pts.length === 0) return [];

    const step = stepMs(granularity, pts[0].x);
    const out = [pts[0]];

    for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const gap = curr.x - prev.x;

        if (gap > step * 1.1) {
            out.push({ x: prev.x + step, y: 0 });

            const before = curr.x - step;
            if (before > prev.x + step) out.push({ x: before, y: 0 });
        }

        out.push(curr);
    }

    return out;
}

function SiteMetricCurve({ rows = [], title, unit = "", infoText = "" }) {
    const { t } = useTranslation();
    const chartTitle = title || t("form.peakPower");
    const [granularity, setGranularity] = useState("Day");
    const granularityOptions = useMemo(
        () => GRANULARITY_OPTIONS.map((value) => ({
            value,
            label: t(`siteStats.granularity.${value}`),
        })),
        [t]
    );

    const data = useMemo(() => {
        if (!Array.isArray(rows) || rows.length === 0) return [];

        const fifteenMin = toFifteenMinPoints(rows);
        if (fifteenMin.length === 0) return [];

        if (granularity === "15 min") {
            return fillGaps(fifteenMin, granularity);
        }

        return fillGaps(maxByPeriod(fifteenMin, granularity), granularity);
    }, [rows, granularity]);

    const { min, max } = useMemo(() => {
        if (data.length === 0) {
            const now = Date.now();
            return { min: now - 60 * 60 * 1000, max: now + 60 * 60 * 1000 };
        }

        if (data.length === 1) {
            const ts = data[0].x;
            const pad = stepMs(granularity, ts);
            return { min: ts - pad, max: ts + pad };
        }

        return {
            min: Math.min(...data.map((p) => p.x)),
            max: Math.max(...data.map((p) => p.x)),
        };
    }, [data, granularity]);

    const options = useMemo(
        () => ({
            chart: {
                type: "area",
                height: 220,
                toolbar: { show: false },
                animations: { enabled: true, speed: 600, easing: "easeinout" },
            },
            stroke: {
                curve: "smooth",
                width: 3,
                colors: chartAreaFill.colors,
            },
            colors: chartAreaFill.colors,
            fill: {
                type: "gradient",
                gradient: {
                    ...chartGradientDefaults,
                    gradientToColors: chartAreaFill.gradientToColors,
                    opacityTo: 0.12,
                },
            },
            markers: { shape: "circle", size: 4, strokeWidth: 2, strokeColors: "#fff" },
            dataLabels: { enabled: false },
            tooltip: {
                x: { formatter: (val) => formatX(val, granularity) },
                y: {
                    formatter: (v) =>
                        Number.isFinite(v) ? `${Number(v).toFixed(1)}${unit ? ` ${unit}` : ""}` : "",
                },
            },
            xaxis: {
                type: "datetime",
                datetimeUTC: false,
                min,
                max,
                labels: { formatter: (val) => formatX(val, granularity) },
            },
            yaxis: {
                labels: { formatter: (v) => Number(v).toFixed(0) },
            },
        }),
        [min, max, unit, granularity]
    );

    return (
        <div className="bg-white p-4 rounded-xl border shadow-lg">
            <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="font-semibold text-sm text-mute-600">{chartTitle}</h4>

                <div className="w-[8.5rem]">
                    <Dropdown
                        options={granularityOptions}
                        selectedOptions={[granularity]}
                        onSelect={(v) => setGranularity(v)}
                    />
                </div>
            </div>

            {infoText ? (
                <p className="mb-3 text-xs text-mute-500">
                    {infoText}
                </p>
            ) : null}

            <Chart options={options} series={[{ name: t("siteStats.max"), data }]} type="area" height={220} />
        </div>
    );
}

export default SiteMetricCurve;
